/*
 * Every add to the cart opens the pull-out cart.
 *
 * The theme already tries to do this: after it redraws the cart it dispatches
 * its own open, but only while it believes the drawer is shut, and that belief
 * is not always right. When it is wrong nothing opens, and the customer is left
 * on the page with no sign that anything went into the cart.
 *
 * So this does not imitate the theme's open. It uses it: the header's cart
 * button carries drawer-to-open="cart-drawer" and is the same button a customer
 * clicks by hand, so clicking it runs the theme's own working path rather than
 * a reconstruction of it.
 *
 * Whether the drawer is open is then read off the page rather than off the
 * theme: a shut drawer is parked outside the window or painted out, and a
 * rectangle does not lie about that the way a flag can. Nothing is clicked
 * while the drawer is already open, because that is what shuts it.
 *
 * This watches every add to the cart, wherever it comes from - the routine
 * module, the theme's own buy button, a quick add, an app - by noticing the
 * request rather than the button.
 */

(function () {
  'use strict';

  var TICK = 150;
  var FIRST_CLICK = 4; /* ~600ms: the theme gets first go */
  var SECOND_CLICK = 12; /* ~1.8s */
  var GIVE_UP = 20;

  var nativeFetch = window.fetch;
  var running = null;

  function component() {
    return document.querySelector('cart-drawer-component');
  }

  /* The theme's own cart button. Only the drawer form of it carries this
     attribute; when the cart is set to a page the header renders a plain link
     instead, which this never matches and so never clicks. */
  function trigger() {
    return document.querySelector('[drawer-to-open="cart-drawer"]');
  }

  /*
   * Is the drawer open? Measured, not asked. A shut drawer is either parked off
   * the edge of the window or painted out; either way the panel's own rectangle
   * and computed style say so, whatever the theme happens to be tracking.
   */
  function isOpen() {
    var wrapper = component();
    if (!wrapper) return false;

    var panel = wrapper.querySelector('.drawer__wrapper');
    if (!panel) {
      return wrapper.isDrawerOpen === true || wrapper.getAttribute('aria-hidden') === 'false';
    }

    var style = window.getComputedStyle(panel);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (Number(style.opacity) === 0) return false;

    var box = panel.getBoundingClientRect();
    if (!box.width || !box.height) return false;

    /* Shut, the panel sits beyond one edge of the window. */
    return box.left < window.innerWidth - 8 && box.right > 8;
  }

  /* Ask the theme to refresh the cart. This redraws the drawer, the header
     count and everything else it keeps in step, and opens the drawer itself
     when it gets that far. */
  function askThemeToRefresh() {
    try {
      var helper = window.HELPER_UTIL;
      var events = window._EVENT_HELPER;
      if (!helper || !events || !events.updateAndShowCart) return false;
      if (!document.querySelector('drawer-cart')) return false;
      helper.dispatchCustomEvent(events.updateAndShowCart, document);
      return true;
    } catch (error) {
      return false;
    }
  }

  /*
   * Redraw the drawer from the section itself. This is the backstop for the
   * above: the theme's redraw reads its response without guarding it, so
   * anything unexpected there leaves the drawer showing the cart as it was when
   * the page loaded - an empty cart, however many items went in.
   */
  function redrawFromSection() {
    var live = document.querySelector('drawer-cart');
    if (!live || typeof nativeFetch !== 'function') return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    nativeFetch(root + '?sections=cart-drawer', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        var html = data && data['cart-drawer'];
        if (!html) return;

        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('drawer-cart');
        if (!fresh) return;

        live.innerHTML = fresh.innerHTML;
        if (fresh.dataset.cartitemcount != null) {
          live.dataset.cartitemcount = fresh.dataset.cartitemcount;
        }
      })
      .catch(function () {
        /* The drawer keeps whatever it had; the cart itself is unaffected. */
      });
  }

  /*
   * Refresh the cart, then see the drawer open. The theme is given the first
   * go; if the drawer is still shut a moment later its own cart button is
   * clicked, and once more after that. Every attempt checks first, so an open
   * drawer is never clicked shut.
   */
  function showCart() {
    if (!component()) return;
    if (running) return;

    askThemeToRefresh();
    setTimeout(redrawFromSection, 900);

    var ticks = 0;
    running = setInterval(function () {
      ticks += 1;

      if (isOpen() || ticks >= GIVE_UP) {
        clearInterval(running);
        running = null;
        return;
      }

      if (ticks === FIRST_CLICK || ticks === SECOND_CLICK) {
        var button = trigger();
        if (button) button.click();
      }
    }, TICK);
  }

  window.routineShowCart = showCart;

  /*
   * Notice the request rather than the button, so this covers every way the
   * cart changes: the routine module, the theme's buy button, a quick add, an
   * app's own button, the quantity steppers, Remove.
   *
   * Only an add opens the drawer. A quantity change or a removal happens with
   * the drawer already in front of the customer, and sliding it open again
   * would be wrong.
   */
  function isAddUrl(url) {
    return String(url || '').indexOf('/cart/add') !== -1;
  }

  var CART_WRITES = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

  function isCartWrite(url) {
    var text = String(url || '');
    for (var i = 0; i < CART_WRITES.length; i++) {
      if (text.indexOf(CART_WRITES[i]) !== -1) return true;
    }
    return false;
  }

  /*
   * Announce the change, with the cart itself when the response carried one.
   * change.js, update.js and add.js all answer with the whole cart, so anything
   * listening can repaint from that rather than going back to ask for a cart it
   * has already been handed - which is the difference between a meter that
   * keeps up and one that lags a beat behind every change.
   */
  function announce(cart) {
    try {
      document.dispatchEvent(new CustomEvent('routine:cart', { detail: { cart: cart || null } }));
    } catch (error) {
      /* a browser without CustomEvent gets the slower path */
    }
  }

  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var adding = isAddUrl(url);
      var writing = isCartWrite(url);
      var result = nativeFetch.apply(this, arguments);

      if (!writing) return result;

      return result.then(function (response) {
        if (!response || !response.ok) return response;

        /* Read a copy, so whoever made the request still gets the body. */
        try {
          response
            .clone()
            .json()
            .then(function (cart) {
              announce(cart && typeof cart.total_price !== 'undefined' ? cart : null);
            })
            .catch(function () {
              announce(null);
            });
        } catch (error) {
          announce(null);
        }

        if (adding) setTimeout(showCart, 0);
        return response;
      });
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      this.routineWatchingAdd = isAddUrl(url);
      this.routineWatchingCart = isCartWrite(url);
    } catch (error) {
      /* a request this cannot label is simply not watched */
    }
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    var request = this;

    if (request.routineWatchingCart) {
      request.addEventListener('load', function () {
        if (request.status < 200 || request.status >= 300) return;

        var cart = null;
        try {
          cart = JSON.parse(request.responseText);
          if (typeof cart.total_price === 'undefined') cart = null;
        } catch (error) {
          cart = null;
        }

        announce(cart);
        if (request.routineWatchingAdd) showCart();
      });
    }

    return nativeSend.apply(this, arguments);
  };
})();
